import React from "react";
import styled from "styled-components";
import { useLocation } from "@reach/router";
import loadable from "@loadable/component";

import { breakpoints, fontFamily, colors } from "../../../theme";
import { PageSection } from "../../components/layout/layout-styles";
import { ContactMeContentFallback } from "../../components/layout/section-skeleton";

import paperPlaneIcon from "../../images/paper-plane.svg";
import lightningIcon from "../../images/lightning.svg";

const ContactFormBlock = loadable(() => import("./contact-form-block"));

const SectionContainer = styled(PageSection)`
  width: 100%;
`;

const Section = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto;
  gap: 42px;
  width: 94%;
  max-width: 1040px;
  margin: 0 auto;
  box-sizing: border-box;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto 1fr auto auto;
    column-gap: 88px;
    row-gap: 42px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: center;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    grid-column: 1;
    grid-row: 1;
    text-align: start;
  }
`;

const Heading = styled.h2`
  display: grid;
  margin: 0;
  font-family: ${fontFamily.extraBold};
  font-size: 32px;
  line-height: 1.02;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 40px;
  }
`;

const HeadingLine = styled.span`
  display: block;
`;

const HeadingLineWithIcon = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    justify-content: flex-start;
  }
`;

const PaperPlaneWrap = styled.span`
  position: relative;
  height: 44px;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 52px;
  }
`;

const PaperPlaneIcon = styled.img`
  position: absolute;
  top: 16px;
  left: 8px;
  height: 100%;
  width: auto;
  display: block;
`;

const Subtitle = styled.p`
  margin: 20px 0 0;
  padding: 0 16px;
  font-size: 20px;
  line-height: 1.2;
  font-family: ${fontFamily.medium};
  font-weight: 500;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    padding: 0;
  }
`;

const AvailabilityBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 11px 17px;
  border-radius: 999px;
  background-color: ${colors.availability.background};
  border: 1px solid ${colors.availability.content};
  color: ${colors.availability.content};
  font-family: ${fontFamily.bold};
  font-size: 16px;
  line-height: 1;
  margin: 20px auto 0;
  max-width: max-content;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 17px;
    margin: 16px 0 0;
  }
`;

const BoltIcon = styled.span`
  width: 21px;
  height: 21px;
  background-color: ${colors.availability.content};
  mask: url(${lightningIcon}) center / contain no-repeat;
  -webkit-mask: url(${lightningIcon}) center / contain no-repeat;
`;

const ContactMeSection = () => {
  const { pathname } = useLocation();
  const isContactPage = pathname.replace(/\/$/, "") === "/contact-me";

  return (
    <SectionContainer
      name="contactMe"
      as="section"
      $bgColor={colors.accentLight}
    >
      <Section>
        <TitleSection>
          <Heading as={isContactPage ? "h1" : "h2"}>
            <HeadingLine>Let's bring your next</HeadingLine>
            <HeadingLineWithIcon>
              Big Idea to life
              <PaperPlaneWrap aria-hidden="true">
                <PaperPlaneIcon src={paperPlaneIcon} alt="" />
              </PaperPlaneWrap>
            </HeadingLineWithIcon>
          </Heading>
          <Subtitle>
            Whether you're brainstorming ideas, looking to bolster capabilities,
            rapidly expanding your team, or delegating projects, count on us to
            meet your needs effectively any or every step of the way
          </Subtitle>

          <AvailabilityBadge>
            <BoltIcon aria-hidden="true" />
            Available for new engagements
          </AvailabilityBadge>
        </TitleSection>

        <ContactFormBlock fallback={<ContactMeContentFallback />} />
      </Section>
    </SectionContainer>
  );
};

export default ContactMeSection;
